import React, { useState } from 'react';
import {
  MARKET_PRICES,
  CURRENT_RATES,
  PROTOCOL_CONSTANTS,
  calcDecision,
  calcExactGain
} from '../constants/pricing';
import { fmtIcp, fmtN } from '../utils/formatters';

const GetNIcpV2Modal = ({ isOpen, onClose, userICP = 0 }) => {
  const [inputAmount, setInputAmount] = useState('');
  const [selectedOption, setSelectedOption] = useState('stake'); // 'stake' or 'swap'
  const [currentStep, setCurrentStep] = useState('selection'); // 'selection' or 'confirmation'

  if (!isOpen) return null;

  const icpAmount = parseFloat(inputAmount) || 0;
  const hasInsufficientFunds = icpAmount > userICP;

  // Calculate decision comparison results
  const icpPrice = MARKET_PRICES.ICP;
  const wtnPrice = MARKET_PRICES.WTN;
  const wtnPer100 = PROTOCOL_CONSTANTS.WTN_PER_100_ICP;

  const decisionResults = calcDecision(
    icpAmount,
    CURRENT_RATES,
    wtnPer100,
    wtnPrice,
    icpPrice
  );
  const decisionExactGain = calcExactGain(decisionResults.market.nIcp, CURRENT_RATES);

  const handleMaxClick = () => {
    setInputAmount(userICP.toString());
  };

  const handleSelectStake = () => {
    setSelectedOption('stake');
  };

  const handleSelectSwap = () => {
    setSelectedOption('swap');
  };

  const handleContinue = () => {
    if (hasInsufficientFunds || icpAmount <= 0) {
      alert('Please enter a valid amount');
      return;
    }
    setCurrentStep('confirmation');
  };

  const handleBack = () => {
    setCurrentStep('selection');
  };

  const handleFinalConfirm = () => {
    if (selectedOption === 'stake') {
      // TODO: Implement staking flow
      console.log('Proceed to stake:', icpAmount, 'ICP');
    } else {
      // TODO: Implement swap flow
      console.log('Proceed to swap:', icpAmount, 'ICP');
    }
    handleClose();
  };

  const handleClose = () => {
    // Reset to initial state when closing
    setCurrentStep('selection');
    setInputAmount('');
    setSelectedOption('stake');
    onClose();
  };

  return (
    <div className="modal-overlay" onClick={handleClose}>
      <div className="get-nicp-v2-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <div className="modal-title">
            <span className="modal-icon">💧</span>
            <h2>{currentStep === 'selection' ? 'Stake ICP' : 'Review transaction'}</h2>
          </div>
          <button className="modal-close" onClick={handleClose}>
            ✕
          </button>
        </div>

        <div className="modal-body">
          {currentStep === 'selection' ? (
            <>
              {/* Input Section */}
              <div className="input-section">
                <div className="input-container">
                  <input
                    type="number"
                    value={inputAmount}
                    onChange={(e) => setInputAmount(e.target.value)}
                    className="amount-input"
                    placeholder="0"
                    min="0"
                    step="0.1"
                  />
                  <div className="input-suffix">
                    <button className="max-button" onClick={handleMaxClick}>
                      MAX
                    </button>
                    <div className="token-info">
                      <span className="token-symbol">ICP</span>
                      <div className="token-icon">∞</div>
                    </div>
                  </div>
                </div>
                {hasInsufficientFunds && icpAmount > 0 && (
                  <div className="error-message">
                    <span className="error-icon">⚠️</span>
                    Insufficient funds
                  </div>
                )}
              </div>

              {/* Option Cards */}
              <div className="option-cards-grid">
                {/* Stake Option Card */}
                <div 
                  className={`option-card-compact ${selectedOption === 'stake' ? 'selected' : ''} ${icpAmount > 0 && !hasInsufficientFunds ? 'clickable' : 'disabled'}`}
                  onClick={handleSelectStake}
                >
                  <div className="card-radio">
                    <div className={`radio-button ${selectedOption === 'stake' ? 'checked' : ''}`}>
                      {selectedOption === 'stake' && <div className="radio-dot"></div>}
                    </div>
                  </div>
                  <div className="card-content-wrapper">
                    <div className="card-header-compact">
                      <h3>🥩 Stake via WaterNeuron</h3>
                      <div className="card-badge bonus">+WTN Airdrop</div>
                    </div>
                    <div className="card-content-compact">
                      <div className="result-amount">
                        {fmtN(decisionResults.stake.nIcp)} nICP
                      </div>
                      <div className="bonus-info">
                        +{decisionResults.stake.wtnCount.toFixed(0)} WTN bonus
                      </div>
                      <div className="description">
                        Stake with WaterNeuron protocol
                      </div>
                    </div>
                  </div>
                </div>

                {/* Market Swap Option Card */}
                <div 
                  className={`option-card-compact ${selectedOption === 'swap' ? 'selected' : ''} ${icpAmount > 0 && !hasInsufficientFunds ? 'clickable' : 'disabled'} ${decisionExactGain.isSignificant && decisionExactGain.isPositive ? 'recommended' : ''}`}
                  onClick={handleSelectSwap}
                >
                  <div className="card-radio">
                    <div className={`radio-button ${selectedOption === 'swap' ? 'checked' : ''}`}>
                      {selectedOption === 'swap' && <div className="radio-dot"></div>}
                    </div>
                  </div>
                  <div className="card-content-wrapper">
                    <div className="card-header-compact">
                      <h3>🔄 Swap to nICP</h3>
                      {decisionExactGain.isSignificant && decisionExactGain.isPositive && (
                        <div className="card-badge best">Best Choice</div>
                      )}
                    </div>
                    <div className="card-content-compact">
                      <div className="result-amount">
                        {fmtN(decisionResults.market.nIcp)} nICP
                      </div>
                      {decisionExactGain.isSignificant && decisionExactGain.isPositive && (
                        <div className="gain-info">
                          +{fmtIcp(decisionExactGain.gainIcp)} ICP gain potential
                        </div>
                      )}
                      <div className="description">
                        Instant purchase from DEX
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Continue Button */}
              <div className="modal-actions">
                <button 
                  className={`continue-button ${hasInsufficientFunds || icpAmount <= 0 ? 'disabled' : ''}`}
                  onClick={handleContinue}
                  disabled={hasInsufficientFunds || icpAmount <= 0}
                >
                  {selectedOption === 'stake' ? 'Continue with Staking' : 'Continue with Swapping'}
                </button>
              </div>

            </>
          ) : (
            <>
              {/* Confirmation Screen */}
              <div className="confirmation-content">
                {/* Transaction Summary Box */}
                <div className="transaction-summary">
                  {/* You Pay */}
                  <div className="transaction-row">
                    <div className="transaction-label">You pay</div>
                    <div className="transaction-amount">
                      <div className="token-display">
                        <div className="token-icon">∞</div>
                        <span className="amount-large">{icpAmount} ICP</span>
                      </div>
                      <div className="usd-value">≈ CHF {(icpAmount * MARKET_PRICES.ICP).toFixed(2)}</div>
                    </div>
                  </div>

                  {/* Arrow */}
                  <div className="transaction-arrow">
                    <div className="arrow-down">↓</div>
                  </div>

                  {/* You Receive */}
                  <div className="transaction-row">
                    <div className="transaction-label">You receive</div>
                    <div className="transaction-amount">
                      <div className="token-display">
                        <div className="token-icon">💧</div>
                        <span className="amount-large">
                          {selectedOption === 'stake' 
                            ? fmtN(decisionResults.stake.nIcp) 
                            : fmtN(decisionResults.market.nIcp)
                          } nICP
                        </span>
                      </div>
                      <div className="usd-value">
                        ≈ CHF {(
                          (selectedOption === 'stake' ? decisionResults.stake.nIcp : decisionResults.market.nIcp) 
                          * MARKET_PRICES.nICP
                        ).toFixed(2)}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Transaction Details */}
                <div className="transaction-details">
                  {selectedOption === 'swap' ? (
                    <>
                      <div className="detail-row">
                        <span>Value difference</span>
                        <span className="positive">-0.57%</span>
                      </div>
                      <div className="detail-row">
                        <span>Max slippage</span>
                        <span>1.5%</span>
                      </div>
                      <div className="detail-row">
                        <span>Swap provider</span>
                        <div className="provider-info">
                          <div className="best-rate-badge">⭐ Best Rate</div>
                          <span>ICPSwap ▼</span>
                        </div>
                      </div>
                      <div className="detail-row">
                        <span>Total estimated fee</span>
                        <span>&lt; CHF 0.01 ▼</span>
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="detail-row">
                        <span>WTN Airdrop</span>
                        <span className="bonus">+{decisionResults.stake.wtnCount.toFixed(0)} WTN</span>
                      </div>
                      <div className="detail-row">
                        <span>Staking method</span>
                        <span>WaterNeuron Protocol</span>
                      </div>
                      <div className="detail-row">
                        <span>Expected APY</span>
                        <span className="positive">~{PROTOCOL_CONSTANTS.STAKING_APY}%</span>
                      </div>
                    </>
                  )}
                </div>

                {/* Action Buttons */}
                <div className="confirmation-actions">
                  <button className="back-button" onClick={handleBack}>
                    Back
                  </button>
                  <button className="confirm-button" onClick={handleFinalConfirm}>
                    {selectedOption === 'stake' ? 'Stake now' : 'Swap now'}
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default GetNIcpV2Modal;
